import {
  CreateParams,
  DataProvider,
  DeleteParams,
  GetListParams,
  GetOneParams,
  GetManyReferenceParams,
  UpdateManyParams,
} from 'react-admin';

import { DataProviderResource } from '../constants/common.constants';
import { DataProviderResourceNotFoundError } from '../errors';

import {
  deleteAccountApplicationDocument,
  downloadAccountApplicationDocument,
  getAccountApplication,
  updateAccountApplication,
} from './accountApplication.provider';
import {
  getAccountBalance,
  getAccountBalanceList,
  getAccountWithdrawalFundsBalance,
} from './accountBalances.providers';
import {
  listAccountHolderUpdateRequests,
  approveAccountHolderUpdateRequest,
  rejectAccountHolderUpdateRequest,
} from './accountHolders.providers';
import {
  downloadAccountAgreement,
  getAccount,
  getAccountList,
  linkAccount,
  moveAccount,
  resetAccountAttempts,
  syncAccount,
  syncAccountStatusList,
  unlinkAccount,
} from './accounts.providers';
import {
  searchAchRelationshipList,
  getAchRelationshipList,
  getAchRelationship,
  approveAchRelationship,
  cancelAchRelationship,
  rejectAchRelationship,
  updateAchRelationship,
} from './achRelationships.providers';
import { createConfig, getConfigList, getConfig, updateConfig, deleteConfig } from './configs.providers';
import {
  cancelFileExport,
  deleteFileExport,
  downloadFileExport,
  getFileExport,
  getFileExportList,
} from './fileExport.provider';
import {
  getInvestigationList,
  submitInvestigationDocument,
  downloadInvestigationDocument,
  appealInvestigation,
  rejectInvestigation,
  deleteInvestigationDocument,
  submitApplicationDocument,
  downloadApplicationDocument,
  deleteApplicationDocument,
  continueApplication,
} from './investigations.providers';
import { getLegacyUserList } from './legacyUser.provider';
import {
  createOfferDocument,
  listOfferDocuments,
  getManyReferenceOfferDocuments,
  getOfferDocument,
  patchOfferDocument,
  deleteOfferDocument,
} from './offerDocuments.providers';
import {
  createOfferOption,
  listOfferOptions,
  getManyOfferOptions,
  getOfferOption,
  patchOfferOption,
  deleteOfferOption,
} from './offerOptions.providers';
import {
  getOfferOrderDetails,
  getOfferOrderList,
  updateOfferOrder,
  updateOfferOrderListStatus,
  deleteOfferOrder,
  createOfferOrderRefund,
  getOfferOrderCardPaymentList,
  getOfferOrderCardPaymentDetails,
  createOfferOrderFileExport,
} from './offerOrders.providers';
import {
  applyOfferOversubscription,
  cancelOfferOversubscription,
  createOfferOversubscription,
  getOfferOversubscription,
  getOfferOversubscriptionDraftOrder,
  getOfferOversubscriptionDraftOrderList,
  getOfferOversubscriptionList,
  updateOfferOversubscriptionDraftOrder,
} from './offerOversubscription.provider';
import {
  createOffer,
  getOfferList,
  getManyOffers,
  getOffer,
  patchOffer,
  completeOffer,
  newOfferCardPaymentAccountLink,
  aggregateOfferOrders,
  aggregateOfferOrderPayments,
} from './offers.providers';
import { getOfferWatchList, getOfferWatch } from './offerWatches.providers';
import { getOrderList } from './orders.providers';
import { getPositionList } from './position.provider';
import {
  createPrivateQuote,
  getPrivateQuoteList,
  snapPrivateQuoteList,
  getPrivateQuote,
  deletePrivateQuote,
  patchPrivateQuote,
} from './privateQuotes.providers';
import {
  searchTransferList,
  getTransferList,
  getTransfer,
  firmApproveTransfer,
  repApproveTransfer,
  rejectTransfer,
  cancelTransfer,
  updateTransfer,
} from './transfers.providers';
import {
  closeUser,
  updateUserMfaPreferences,
  getUser,
  getUserList,
  updateUserPostcloseOffers,
  lockUser,
  unlockUser,
  forgotPassword,
  updateUserDashboardSettings,
} from './users.providers';

const createProvider = async (resource: string, params: CreateParams) => {
  switch (resource) {
    case 'offers':
      return createOffer(params);
    case 'offer-options':
      return createOfferOption(params);
    case 'offer-documents':
      return createOfferDocument(params);
    case 'configs':
      return createConfig(params);
    case 'private-quotes':
      return createPrivateQuote(params);
    case DataProviderResource.OfferOrderRefunds:
      return createOfferOrderRefund(params);
    case DataProviderResource.CreateOfferOrderFileExport:
      return createOfferOrderFileExport(params);
    case DataProviderResource.OfferOversubscription:
      return createOfferOversubscription(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const getOneProvider = async (resource: string, params: GetOneParams) => {
  switch (resource) {
    case DataProviderResource.Users:
      return getUser(params);
    case DataProviderResource.Accounts:
      return getAccount(params);
    case 'achrelationships':
      return getAchRelationship(params);
    case DataProviderResource.AccountAgreements:
      return downloadAccountAgreement(params);
    case 'achtransfers':
      return getTransfer(params);
    case 'offers':
      return getOffer(params);
    case 'offer-options':
      return getOfferOption(params);
    case 'offer-documents':
      return getOfferDocument(params);
    case DataProviderResource.AggregateOfferOrders:
      return aggregateOfferOrders(params);
    case DataProviderResource.AggregateOfferOrderPayments:
      return aggregateOfferOrderPayments(params);
    case DataProviderResource.OfferOrders:
      return getOfferOrderDetails(params);
    case DataProviderResource.OfferOrderCardPayments:
      return getOfferOrderCardPaymentDetails(params);
    case DataProviderResource.OfferOversubscription:
      return getOfferOversubscription(params);
    case DataProviderResource.OfferOversubscriptionDraftOrder:
      return getOfferOversubscriptionDraftOrder(params);
    case 'indication-interests':
      return getOfferWatch(params);
    case 'configs':
      return getConfig(params);
    case 'balances':
      return getAccountBalance(params);
    case DataProviderResource.AccountWithdrawalFundsBalance:
      return getAccountWithdrawalFundsBalance(params);
    case 'private-quotes':
      return getPrivateQuote(params);
    case 'application-document':
      return downloadApplicationDocument(params);
    case 'investigation-document':
      return downloadInvestigationDocument(params);
    case DataProviderResource.FileExport:
      return getFileExport(params);
    case DataProviderResource.DownloadFileExport:
      return downloadFileExport(params);
    case DataProviderResource.AccountApplication:
      return getAccountApplication(params);
    case DataProviderResource.DownloadAccountApplicationDocument:
      return downloadAccountApplicationDocument(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const getListProvider = async (resource: string, params: GetListParams) => {
  switch (resource) {
    case DataProviderResource.Users:
      return getUserList(params);
    case DataProviderResource.Accounts:
      return getAccountList(params);
    case DataProviderResource.LegacyUsers:
      return getLegacyUserList(params);
    case 'achrelationships':
      return searchAchRelationshipList(params);
    case 'account-achrelationships':
      return getAchRelationshipList(params);
    case 'achtransfers':
      return searchTransferList(params);
    case DataProviderResource.Transfers:
      return getTransferList(params);
    case 'offers':
      return getOfferList(params);
    case 'offer-options':
      return listOfferOptions(params);
    case 'offer-documents':
      return listOfferDocuments(params);
    case DataProviderResource.OfferOversubscription:
      return getOfferOversubscriptionList(params);
    case DataProviderResource.OfferOversubscriptionDraftOrder:
      return getOfferOversubscriptionDraftOrderList(params);
    case DataProviderResource.OfferOrders:
      return getOfferOrderList(params);
    case 'indication-interests':
      return getOfferWatchList(params);
    case 'update-requests':
      return listAccountHolderUpdateRequests(params);
    case 'account-investigations':
      return getInvestigationList(params);
    case 'configs':
      return getConfigList(params);
    case DataProviderResource.OfferOrderCardPayments:
      return getOfferOrderCardPaymentList(params);
    case DataProviderResource.Orders:
      return getOrderList(params);
    case 'orders.positions':
      return getPositionList(params);
    case 'private-quotes':
      return getPrivateQuoteList(params);
    case DataProviderResource.FileExport:
      return getFileExportList(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const getManyProvider = async (resource: string, params: any) => {
  switch (resource) {
    case 'offers':
      return getManyOffers(params);
    case 'offer-options':
      return getManyOfferOptions(params);
    case 'offer-documents':
      return listOfferDocuments(params);
    case 'indication-interests':
      return getOfferWatchList(params);
    case 'configs':
      return getConfigList(params);
    case 'private-quotes':
      return snapPrivateQuoteList(params);
    case 'balances':
      return getAccountBalanceList(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const getManyReferenceProvider = async (resource: string, params: GetManyReferenceParams) => {
  switch (resource) {
    case 'offer-documents':
      return getManyReferenceOfferDocuments(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const updateProvider = async (resource: string, params: any) => {
  switch (resource) {
    case DataProviderResource.SyncAccount:
      return syncAccount(params);
    case DataProviderResource.LinkAccount:
      return linkAccount(params);
    case DataProviderResource.UnlinkAccount:
      return unlinkAccount(params);
    case DataProviderResource.MoveAccount:
      return moveAccount(params);
    case DataProviderResource.ResetAccountAttempts:
      return resetAccountAttempts(params);
    case 'ACHRelationship.EDIT':
      return updateAchRelationship(params);
    case 'ACHRelationship.CANCEL':
      return cancelAchRelationship(params);
    case 'ACHRelationship.APPROVE':
      return approveAchRelationship(params);
    case 'ACHRelationship.REJECT':
      return rejectAchRelationship(params);
    case 'ACHTransfer.EDIT':
      return updateTransfer(params);
    case 'ACHTransfer.CANCEL':
      return cancelTransfer(params);
    case 'ACHTransfer.REP_APPROVE':
      return repApproveTransfer(params);
    case 'ACHTransfer.FIRM_APPROVE':
      return firmApproveTransfer(params);
    case 'ACHTransfer.REJECT':
      return rejectTransfer(params);
    case 'offers':
      return patchOffer(params);
    case DataProviderResource.CompleteOffer:
      return completeOffer(params);
    case 'offer-options':
      return patchOfferOption(params);
    case 'offer-documents':
      return patchOfferDocument(params);
    case DataProviderResource.ApplyOfferOversubscription:
      return applyOfferOversubscription(params);
    case DataProviderResource.CancelOfferOversubscription:
      return cancelOfferOversubscription(params);
    case DataProviderResource.OfferOversubscriptionDraftOrder:
      return updateOfferOversubscriptionDraftOrder(params);
    case DataProviderResource.OfferOrders:
      return updateOfferOrder(params);
    case DataProviderResource.NewOfferCardPaymentAccountLink:
      return newOfferCardPaymentAccountLink(params);
    case DataProviderResource.LockUser:
      return lockUser(params);
    case DataProviderResource.UnlockUser:
      return unlockUser(params);
    case DataProviderResource.CloseUser:
      return closeUser(params);
    case DataProviderResource.UpdateUserPostcloseOffers:
      return updateUserPostcloseOffers(params);
    case DataProviderResource.UpdateUserDashboardSettings:
      return updateUserDashboardSettings(params);
    case DataProviderResource.UpdateUserMfaPreferences:
      return updateUserMfaPreferences(params);
    case DataProviderResource.ForgotPassword:
      return forgotPassword(params);
    case 'update-requests.APPROVE':
      return approveAccountHolderUpdateRequest(params);
    case 'update-requests.REJECT':
      return rejectAccountHolderUpdateRequest(params);
    case 'investigation-document':
      return submitInvestigationDocument(params);
    case 'investigation.APPEAL':
      return appealInvestigation(params);
    case 'investigation.REJECT':
      return rejectInvestigation(params);
    case 'application-document':
      return submitApplicationDocument(params);
    case 'application.CONTINUE':
      return continueApplication(params);
    case 'configs':
      return updateConfig(params);
    case 'private-quotes':
      return patchPrivateQuote(params);
    case DataProviderResource.CancelFileExport:
      return cancelFileExport(params);
    case DataProviderResource.AccountApplication:
      return updateAccountApplication(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const updateManyProvider = async (resource: string, params: UpdateManyParams) => {
  switch (resource) {
    case DataProviderResource.OfferOrdersStatus:
      return updateOfferOrderListStatus(params);
    case DataProviderResource.AccountSyncStatusList:
      return syncAccountStatusList(params);
    default:
  }
};

const deleteProvider = async (resource: string, params: DeleteParams) => {
  switch (resource) {
    case 'offer-options':
      return deleteOfferOption(params);
    case 'offer-documents':
      return deleteOfferDocument(params);
    case 'investigation-document':
      return deleteInvestigationDocument(params);
    case 'application-document':
      return deleteApplicationDocument(params);
    case 'configs':
      return deleteConfig(params);
    case 'private-quotes':
      return deletePrivateQuote(params);
    case DataProviderResource.OfferOrders:
      return deleteOfferOrder(params);
    case DataProviderResource.FileExport:
      return deleteFileExport(params);
    case DataProviderResource.DeleteAccountApplicationDocument:
      return deleteAccountApplicationDocument(params);
    default:
      throw new DataProviderResourceNotFoundError(resource);
  }
};

const dataProvider = {
  create: createProvider,
  getOne: getOneProvider,
  getList: getListProvider,
  getMany: getManyProvider,
  getManyReference: getManyReferenceProvider,
  update: updateProvider,
  updateMany: updateManyProvider,
  delete: deleteProvider,
} as DataProvider;

export default dataProvider;
